/**
 * How a subscription status is coloured.
 *
 * Billing status is read at a glance, so "active" and "past due" have to be
 * separable without reading the word. When both fills lived in `styles.css` they
 * once resolved to the same neutral background in every theme, leaving the two
 * states distinguishable only by their text — which is what `designTokens.test`
 * has guarded ever since.
 *
 * The mapping is data rather than a stylesheet so that guard can assert on the
 * decision itself: which canonical token each status resolves to, checked
 * against the real palette per theme, instead of a regex over CSS text.
 */

/** The `--nim-*` token a status is painted from, or null for the neutral fill. */
export type BillingTone = '--nim-success' | '--nim-error' | null;

const TONE_BY_STATUS: Record<string, BillingTone> = {
  active: '--nim-success',
  trialing: '--nim-success',
  past_due: '--nim-error',
  unpaid: '--nim-error',
  canceled: '--nim-error',
};

export function billingStatusTone(status: string): BillingTone {
  return TONE_BY_STATUS[status] ?? null;
}

const BADGE_BASE =
  'status-badge mt-[0.7rem] inline-flex rounded-full px-[0.55rem] py-1 '
  + 'text-[0.68rem] font-medium capitalize';

/**
 * Tint mixed down from the canonical semantic colour rather than declared as its
 * own token, so it tracks whatever the active theme sets.
 *
 * Written out per tone rather than interpolated, because Tailwind scans source
 * *text* for class names. A `bg-[color-mix(...)]` utility built by dropping a
 * template placeholder into the arbitrary value was emitted with the
 * placeholder still in it, and the resulting rule crashed the production
 * build's CSS minifier on an unexpected `$`. It showed up in neither dev, where
 * the CSS is not minified, nor in a jsdom test, which asserts on the tone
 * rather than on the stylesheet.
 *
 * The scan is text, not syntax: it does not skip comments either, so do not
 * quote the broken class here to explain it. An arbitrary value has to be a
 * literal wherever it appears in this file; the lookup is where a value varies.
 */
const BADGE_CLASS_BY_TONE: Record<NonNullable<BillingTone>, string> = {
  '--nim-success':
    'text-[color:var(--nim-success)] bg-[color-mix(in_srgb,var(--nim-success)_12%,transparent)]',
  '--nim-error':
    'text-[color:var(--nim-error)] bg-[color-mix(in_srgb,var(--nim-error)_12%,transparent)]',
};

export function billingStatusBadgeClass(status: string): string {
  const tone = billingStatusTone(status);
  if (tone === null) return `${BADGE_BASE} bg-nim-tertiary text-nim-muted`;
  return `${BADGE_BASE} ${BADGE_CLASS_BY_TONE[tone]}`;
}
